'use client';

import { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import { Grid, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';

import NavBlogCard from '@/components/Header/Navigation/NavDropdown/NavBlogCard';
import NavItemCard from '@/components/Header/Navigation/NavDropdown/NavItemCard';
import { useLatestBlogPosts } from '@/components/Header/Navigation/NavDropdown/context/LatestBlogPostsContext';
import LinkComponent from '@/components/Link';
import { type NavLinkWithChildren, navExpertiseCardConfig, navServicesCardConfig } from '@/config/navigation.config';

import styles from './NavDropdownPanel.module.scss';

export interface NavDropdownPanelProps {
  link: NavLinkWithChildren;
  open: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onCloseComplete?: () => void;
}

const PANEL_ANIMATION_DURATION = 0.28;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const NavDropdownPanel = ({
  link,
  open,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onCloseComplete,
}: NavDropdownPanelProps) => {
  const t = useTranslations('common');
  const latestBlogPosts = useLatestBlogPosts();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);
  const isServices = link.href === '/services';
  const isExpertise = link.href === '/expertise';
  const servicesCardConfig = isServices ? navServicesCardConfig : null;
  const expertiseCardConfig = isExpertise ? navExpertiseCardConfig : null;

  useGSAP(
    () => {
      const target = dropdownRef.current;

      if (!target) return;

      if (prefersReducedMotion() && open) {
        gsap.set(target, { opacity: 1, y: 0 });

        return;
      }

      if (prefersReducedMotion()) {
        onCloseComplete?.();

        return;
      }

      if (open) {
        wasOpenRef.current = true;
        gsap.fromTo(
          target,
          { opacity: 0, y: 8 },
          { opacity: 1, y: 0, duration: PANEL_ANIMATION_DURATION, ease: 'power2.out' }
        );

        return;
      }

      if (!wasOpenRef.current) return;

      wasOpenRef.current = false;
      gsap.to(target, {
        opacity: 0,
        y: 8,
        duration: PANEL_ANIMATION_DURATION,
        ease: 'power2.in',
        onComplete: () => onCloseComplete?.(),
      });
    },
    { scope: dropdownRef, dependencies: [open] }
  );

  const renderTitle = (title: string, href: string, text: string) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h5" component="p">
        {title}
      </Typography>
      <LinkComponent href={href} text={text} />
    </Stack>
  );

  const renderLatestNews = () => (
    <Stack className={styles.latestNews}>
      <Stack>{renderTitle(t('navDropdown.latestBlogsTitle'), '/blog', t('navDropdown.readMoreBlogs'))}</Stack>
      <Grid container mt={3} spacing={2}>
        {latestBlogPosts.slice(0, 2).map(post => (
          <Grid size={6} key={post.slug}>
            <NavBlogCard
              slug={post.slug}
              title={post.title}
              cardImage={post.cardImage}
              date={post.date}
              readTime={post.readTime}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const renderContent = () => {
    if (servicesCardConfig) {
      return (
        <Stack direction="row" alignItems="flex-start" gap={3}>
          <Stack className={styles.services}>
            <Stack>{renderTitle(t('navigation.services'), '/services', t('navDropdown.viewAllServices'))}</Stack>
            <Grid container mt={3}>
              {link.children.map(child => {
                const config = servicesCardConfig[child.href];

                if (!config) {
                  return null;
                }

                return (
                  <Grid size={6} key={child.href}>
                    <NavItemCard child={child} config={config} />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
          <Stack className={styles.latestNews}>{renderLatestNews()}</Stack>
        </Stack>
      );
    }

    if (expertiseCardConfig) {
      return (
        <Stack direction="row" alignItems="flex-start" gap={3}>
          <Stack className={styles.services}>
            <Stack>{renderTitle(t('navigation.expertise'), '/expertise', t('navDropdown.ourExpertise'))}</Stack>
            <Grid container mt={3}>
              {link.children.map(child => {
                const config = expertiseCardConfig[child.href];

                if (!config) {
                  return null;
                }

                return (
                  <Grid size={6} key={child.href}>
                    <NavItemCard child={child} config={config} />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
          <Stack className={styles.latestNews}>{renderLatestNews()}</Stack>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Box className={styles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>
      <Box ref={dropdownRef} className={styles.dropdown} role="menu">
        {renderContent()}
      </Box>
    </Box>
  );
};

export default NavDropdownPanel;
